/**
 * Reset password: /reset/:token
 *
 * Step 2 of two — the page the emailed link opens. Public, like /join and
 * /pay: the member cannot sign in, that is the entire problem.
 *
 * The link is ONE-TIME and short-lived (data/reset.js), and each way it can
 * be dead gets its own page. "This link expired" and "this link was already
 * used" send a member to different next steps, and a single "invalid link"
 * would have half of them asking for a second email they do not need.
 *
 * On success it does NOT sign them in. They go back to the sign-in screen and
 * use the new password — which is the one thing that proves it took, on the
 * screen they will use every time after this.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PASSWORD_MIN, passwordProblem, RESET_TTL_MIN } from '../data';
import { useClub } from '../context/ClubContext';
import { INPUT, SubmitButton } from './SignIn';
import { Shell } from './ForgotPassword';

export function ResetPasswordPage() {
  const { token } = useParams();
  const { resetPasswordWithToken } = useClub();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  /*
   * WHY THE DEAD-LINK PAGE IS NOW AN OUTCOME, NOT A PRECHECK.
   *
   * This screen used to read the token's state out of the club held in the
   * browser and refuse a dead link before the member typed anything. It cannot:
   * reset documents are unreadable by EVERY client, admin included, because a
   * readable token is a usable token and holding one is enough to take over the
   * account it belongs to.
   *
   * So the form opens, and the server's verdict arrives on submit. `LINK_DEAD`
   * carries a message already written for the member — expired, already used,
   * or superseded by a newer email all send them somewhere different, and a
   * single "invalid link" would have half of them asking for a second email
   * they do not need.
   */
  const [dead, setDead] = useState(null);

  if (done) {
    return (
      <Shell>
        <div className="inline-flex items-center gap-2 rounded-full bg-sea-50 px-3 py-1.5 font-market text-[9.5px] uppercase tracking-wide text-sea-600">
          <span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-sea-600 text-[8px] text-white">
            ✓
          </span>
          Password set
        </div>
        <h1 className="mt-3 font-market text-[27px] font-extrabold leading-tight tracking-[-0.03em]">
          You’re all set{done.name ? `, ${done.name.split(' ')[0]}` : ''}
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">
          Your new password is saved. Sign in with your mobile number or your email and the
          password you just chose.
        </p>
        <button
          type="button"
          onClick={() => navigate('/signin')}
          className="mt-5 flex w-full items-center justify-center bg-gold-500 py-3.5 font-market text-[15.5px] font-bold tracking-[-0.015em] text-ink-800 transition-all duration-200 hover:brightness-110"
        >
          Go to sign in
        </button>
        <p className="mt-3 text-center font-market text-[9.5px] leading-relaxed text-copy-label">
          That link has now been used and will not open again.
        </p>
      </Shell>
    );
  }

  if (dead) {
    return (
      <Shell>
        <div className="inline-flex items-center rounded-full bg-line-chip px-3 py-1.5 font-market text-[9.5px] uppercase tracking-wide text-copy-muted">
          Link not usable
        </div>
        <h1 className="mt-3 font-market text-[27px] font-extrabold leading-tight tracking-[-0.03em]">
          This link no longer works
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">{dead}</p>
        <Link
          to="/forgot"
          className="mt-5 flex w-full items-center justify-center bg-gold-500 py-3.5 font-market text-[15.5px] font-bold tracking-[-0.015em] text-ink-800 transition-all duration-200 hover:brightness-110"
        >
          Send me a new link
        </Link>
      </Shell>
    );
  }

  async function submit(e) {
    e?.preventDefault();
    if (busy) return;

    const problem = passwordProblem(password);
    if (problem) return setError(problem);
    if (password !== confirm) return setError('The two passwords do not match.');

    setError('');
    setBusy(true);
    try {
      await resetPasswordWithToken(token, password);
      setDone({});
      window.scrollTo(0, 0);
    } catch (err) {
      // A dead link is not a form error — it needs the panel above, with its
      // own way out.
      if (err?.code === 'LINK_DEAD') {
        setDead(err.message);
        window.scrollTo(0, 0);
        return;
      }
      setError(err?.message ?? 'Could not set that password.');
      setBusy(false);
    }
  }

  return (
    <Shell step={3}>
      <h1 className="font-market text-[27px] font-extrabold leading-none tracking-[-0.03em]">
        Set a new password
      </h1>
      <p className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">
        Choose the password you’ll sign in with from now on — with your mobile number or your
        email, either one.
      </p>

      <form onSubmit={submit} className="mt-6">
        <div className="mb-4">
          <label
            htmlFor="r-pw"
            className="mb-1.5 block font-market text-[9.5px] uppercase tracking-[0.09em] text-copy-muted"
          >
            New password
          </label>
          <div className="relative flex items-center">
            <svg
              className="pointer-events-none absolute left-3.5"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <rect x="4" y="10" width="16" height="11" rx="2.5" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            <input
              id="r-pw"
              type={show ? 'text' : 'password'}
              autoComplete="off"
              autoFocus
              placeholder="••••••••"
              className={INPUT}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
            />
            <button
              type="button"
              className="absolute right-2 px-2 py-1.5 font-market text-[9.5px] uppercase tracking-wide text-copy-label hover:text-copy"
              onClick={() => setShow((s) => !s)}
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="r-pw2"
            className="mb-1.5 block font-market text-[9.5px] uppercase tracking-[0.09em] text-copy-muted"
          >
            Repeat it
          </label>
          <div className="relative flex items-center">
            <svg
              className="pointer-events-none absolute left-3.5"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <rect x="4" y="10" width="16" height="11" rx="2.5" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
            <input
              id="r-pw2"
              type={show ? 'text' : 'password'}
              autoComplete="off"
              placeholder="••••••••"
              className={INPUT}
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                setError('');
              }}
            />
          </div>
        </div>

        <SubmitButton busy={busy} label="Save password" busyLabel="Saving…" />
        {error && <div className="mt-2.5 font-market text-[11px] text-flag-600">{error}</div>}

        <p className="mt-3 font-market text-[9.5px] leading-relaxed text-copy-label">
          At least {PASSWORD_MIN} characters, including a number. This link works once and expires{' '}
          {RESET_TTL_MIN} minutes after it was sent.
        </p>
      </form>
    </Shell>
  );
}
